import i18n, { type BackendModule, type ResourceKey } from "i18next";
import { initReactI18next } from "react-i18next";
import { usePreferences, type Language } from "./preferences-store.js";

export const DEFAULT_LANGUAGE: Language = "pt-BR";
export const SUPPORTED_LANGUAGES: readonly Language[] = ["pt-BR", "en", "es"];

export function isSupportedLanguage(value: unknown): value is Language {
  return typeof value === "string" && SUPPORTED_LANGUAGES.includes(value as Language);
}

export function normalizeLanguage(value: unknown): Language {
  if (isSupportedLanguage(value)) return value;
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    if (normalized.startsWith("pt")) return "pt-BR";
    if (normalized.startsWith("en")) return "en";
    if (normalized.startsWith("es")) return "es";
  }
  return DEFAULT_LANGUAGE;
}

// Cada catálogo passa de 240 KB de fonte. Com os três importados estaticamente,
// dois terços disso viajavam no caminho crítico de um usuário que lê um idioma
// só. O import() dá um chunk por idioma; o bundler resolve o cache e o i18next
// guarda o namespace já lido, então trocar de ida e volta não baixa de novo.
const catalogs: Record<Language, () => Promise<ResourceKey>> = {
  "pt-BR": () => import("./locales/pt-BR.js").then((m) => m.ptBR),
  en: () => import("./locales/en.js").then((m) => m.en),
  es: () => import("./locales/es.js").then((m) => m.es),
};

// Backend em vez de `resources`: assim é o próprio changeLanguage() que espera o
// catálogo chegar antes de trocar o idioma ativo — a UI nunca fica meio traduzida.
const lazyCatalogs: BackendModule = {
  type: "backend",
  init: () => {},
  read: (language, _namespace, callback) => {
    void catalogs[normalizeLanguage(language)]().then(
      (resource) => callback(null, resource),
      (error: unknown) =>
        callback(error instanceof Error ? error : new Error(String(error)), false),
    );
  },
};

const initialLanguage = normalizeLanguage(usePreferences.getState().language);

// A promessa do init é exportada porque agora o primeiro catálogo chega por rede:
// quem monta a árvore (src/main.tsx) espera aqui para não pintar a chave crua.
export const i18nReady = i18n
  .use(lazyCatalogs)
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    // Sem fallback entre idiomas: ele obrigaria a baixar pt-BR junto de en/es e
    // devolveria ao caminho crítico metade do peso que o carregamento sob demanda
    // acabou de tirar. A paridade de chaves entre os três catálogos é garantida por
    // teste (src/i18n.test.ts), então não há chave faltante para o fallback cobrir.
    fallbackLng: false,
    supportedLngs: SUPPORTED_LANGUAGES,
    load: "currentOnly",
    interpolation: { escapeValue: false },
  });

// O idioma inicial já está disponível no store persistido; aplicá-lo aqui evita
// que o primeiro frame seja anunciado pelo navegador com o idioma do HTML base.
if (typeof document !== "undefined") document.documentElement.lang = initialLanguage;

export default i18n;
